/**
 * WorkflowTemplateManager - Manage workflow templates
 * Stitch design system
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Dialog, TextField, Switch, FormControlLabel,
  Select, MenuItem, FormControl, Accordion, AccordionSummary, AccordionDetails,
  Tooltip,
} from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const WORKFLOW_TYPES = [
  { value: 'offertefase', label: 'Offertefase' },
  { value: 'preproductie', label: 'Pre-productie' },
  { value: 'productie', label: 'Productie' },
  { value: 'afronding', label: 'Afronding' },
  { value: 'evaluatie', label: 'Evaluatie' },
];

const ROLES = [
  'Projectleider', 'Accountmanager', 'Regisseur', 'Camera Operator',
  'Producer', 'Editor', 'Animator', 'Copywriter', 'Productie-assistent', 'Management',
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem', fontSize: '0.82rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody,
    '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
    '&.Mui-focused fieldset': { borderColor: T.primary },
  },
  '& .MuiInputLabel-root': { fontFamily: T.fontBody, fontSize: '0.82rem' },
};

const WorkflowTemplateManager = ({ open, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [templateTree, setTemplateTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategories, setEditCategories] = useState('A,B,C');
  const [editType, setEditType] = useState('preproductie');
  const [editActive, setEditActive] = useState(true);

  useEffect(() => { if (open) loadTemplates(); }, [open]);

  const loadTemplates = async () => {
    try { setLoading(true); const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates`); const d = await leesJson(r, 'Templates laden'); setTemplates(d.templates || []); }
    catch (e) { setError(e.message || 'Kon templates niet laden'); }
    finally { setLoading(false); }
  };

  const loadTemplate = async (id) => {
    try {
      setLoading(true); setSelectedId(id);
      const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates/${id}`);
      const d = await leesJson(r, 'Template laden');
      if (d.template) { setTemplateTree(d.template); setEditName(d.template.name); setEditDescription(d.template.description || ''); setEditCategories(d.template.project_categories); setEditType(d.template.workflow_type); setEditActive(d.template.is_active); }
    } catch (e) { setError(e.message || 'Kon template niet laden'); }
    finally { setLoading(false); }
  };

  const handleSaveTemplate = async () => {
    if (!selectedId) return;
    try {
      setSaving(true); setError(null);
      const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates/${selectedId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: editName, description: editDescription, project_categories: editCategories, workflow_type: editType, is_active: editActive, sort_order: templateTree?.sort_order || 0 }) });
      const d = await leesJson(r, 'Template opslaan');
      if (d.success) { setSuccess('Template opgeslagen'); loadTemplates(); setTimeout(() => setSuccess(null), 2000); }
    } catch (err) { setError('Opslaan mislukt: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleAddPhase = async () => {
    if (!selectedId) return;
    try { const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates/phases`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ template_id: selectedId, name: 'Nieuwe fase', sort_order: (templateTree?.phases?.length || 0) }) }); const d = await leesJson(r, 'Fase toevoegen'); if (d.success) loadTemplate(selectedId); }
    catch (e) { setError(e.message || 'Fase toevoegen mislukt'); }
  };

  const handleAddTask = async (phaseId) => {
    try { const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates/tasks`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phase_id: phaseId, name: 'Nieuwe taak', role: 'Projectleider', estimated_hours: 1, sort_order: 0 }) }); const d = await leesJson(r, 'Taak toevoegen'); if (d.success) loadTemplate(selectedId); }
    catch (e) { setError(e.message || 'Taak toevoegen mislukt'); }
  };

  const handleUpdateTask = async (taskId, fields) => {
    const currentTask = templateTree?.phases?.flatMap(p => p.tasks || [])?.find(t => t.id === taskId);
    if (!currentTask) return;
    try { const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates/tasks/${taskId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...currentTask, ...fields }) }); const d = await leesJson(r, 'Taak bijwerken'); if (d.success) loadTemplate(selectedId); }
    catch (e) { setError(e.message || 'Taak bijwerken mislukt'); }
  };

  const handleDeleteTask = async (taskId) => { try { await authFetch(`${BACKEND_URL}/Api/workflow-templates/tasks/${taskId}`, { method: 'DELETE' }); loadTemplate(selectedId); } catch { setError('Taak verwijderen mislukt'); } };
  const handleDeletePhase = async (phaseId) => { try { await authFetch(`${BACKEND_URL}/Api/workflow-templates/phases/${phaseId}`, { method: 'DELETE' }); loadTemplate(selectedId); } catch { setError('Fase verwijderen mislukt'); } };

  const handleDuplicate = async () => {
    if (!templateTree) return;
    try {
      setSaving(true);
      const r = await authFetch(`${BACKEND_URL}/Api/workflow-templates`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: `${templateTree.name} (kopie)`, description: templateTree.description, project_categories: templateTree.project_categories, workflow_type: templateTree.workflow_type, sort_order: templates.length + 1 }) });
      const d = await leesJson(r, 'Template dupliceren');
      if (d.success) { setSuccess('Template gedupliceerd'); loadTemplates(); setTimeout(() => setSuccess(null), 3000); }
    } catch (e) { setError(e.message || 'Dupliceren mislukt'); }
    finally { setSaving(false); }
  };

  const groupedTemplates = WORKFLOW_TYPES.map(wt => ({ ...wt, templates: templates.filter(t => t.workflow_type === wt.value) })).filter(g => g.templates.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: '0.75rem', height: '85vh', overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: `1px solid ${T.surfaceLow}` }}>
        <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text }}>
          Workflow Templates
        </Typography>
        <Box onClick={onClose} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
          <Icon name="close" size={20} sx={{ color: T.outline }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left: Template list */}
        <Box sx={{ width: 260, borderRight: `1px solid ${T.surfaceLow}`, overflow: 'auto', flexShrink: 0, bgcolor: '#fbfbfc', pt: 1 }}>
          {loading && !templates.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Box sx={{ width: 20, height: 20, border: `2px solid ${T.outlineVariant}`, borderTop: `2px solid ${T.tertiary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
            </Box>
          ) : (
            groupedTemplates.map(group => (
              <Box key={group.value}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.54rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outlineVariant, px: 2, pt: 1.5, pb: 0.5 }}>
                  {group.label}
                </Typography>
                {group.templates.map(t => (
                  <Box
                    key={t.id}
                    onClick={() => loadTemplate(t.id)}
                    sx={{
                      mx: 1, px: 1.25, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer',
                      bgcolor: selectedId === t.id ? T.surface : 'transparent',
                      boxShadow: selectedId === t.id ? T.shadow : 'none',
                      transition: 'all 0.12s',
                      '&:hover': { bgcolor: selectedId === t.id ? T.surface : 'rgba(45,51,53,0.03)' },
                    }}
                  >
                    <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: selectedId === t.id ? 700 : 500, color: selectedId === t.id ? T.tertiary : T.secondary }}>
                      {t.name}
                    </Typography>
                    <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant }}>
                      {t.project_categories}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))
          )}
        </Box>

        {/* Right: Template editor */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {/* Alerts */}
          {error && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.errorBg, border: `1px solid ${T.error}20` }}>
              <Icon name="error" size={18} sx={{ color: T.error }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.error, flex: 1 }}>{error}</Typography>
              <Box onClick={() => setError(null)} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}><Icon name="close" size={14} sx={{ color: T.error }} /></Box>
            </Box>
          )}
          {success && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.emeraldBg, border: `1px solid ${T.emerald}20` }}>
              <Icon name="check_circle" size={18} sx={{ color: T.emerald }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.emerald }}>{success}</Typography>
            </Box>
          )}

          {!selectedId ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', color: T.outlineVariant }}>Selecteer een template om te bewerken</Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Box sx={{ width: 24, height: 24, border: `2px solid ${T.outlineVariant}`, borderTop: `2px solid ${T.tertiary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
            </Box>
          ) : (
            <>
              {/* Metadata */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                <TextField label="Naam" value={editName} onChange={(e) => setEditName(e.target.value)} size="small" sx={{ flex: 1, minWidth: 180, ...inputSx }} />
                <TextField label="Beschrijving" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} size="small" sx={{ flex: 2, minWidth: 180, ...inputSx }} />
                <TextField label="Categorieën" value={editCategories} onChange={(e) => setEditCategories(e.target.value)} size="small" sx={{ width: 110, ...inputSx }} helperText="A,B,C" />
                <FormControl size="small" sx={{ minWidth: 130, ...inputSx }}>
                  <Select value={editType} onChange={(e) => setEditType(e.target.value)} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>
                    {WORKFLOW_TYPES.map(wt => <MenuItem key={wt.value} value={wt.value}>{wt.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControlLabel control={<Switch checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />} label={<Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem' }}>Actief</Typography>} />
              </Box>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Box onClick={!saving ? handleSaveTemplate : undefined} sx={{
                  px: 2, py: 0.6, borderRadius: '0.5rem', cursor: !saving ? 'pointer' : 'default',
                  background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
                  color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.75rem',
                  display: 'flex', alignItems: 'center', gap: 0.5, opacity: saving ? 0.7 : 1,
                  boxShadow: '0 4px 12px rgba(99,89,131,0.25)', transition: 'all 0.2s',
                  '&:hover': !saving ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' } : {},
                }}>
                  <Icon name="save" size={14} sx={{ color: '#fff' }} /> Opslaan
                </Box>
                <Box onClick={handleDuplicate} sx={{
                  px: 2, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer',
                  border: `1.5px solid ${T.outlineVariant}40`, color: T.secondary,
                  fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.75rem',
                  display: 'flex', alignItems: 'center', gap: 0.5, transition: 'all 0.15s',
                  '&:hover': { bgcolor: T.surfaceLow },
                }}>
                  <Icon name="content_copy" size={14} sx={{ color: T.outline }} /> Dupliceer
                </Box>
                <Box onClick={handleAddPhase} sx={{
                  px: 2, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer',
                  border: `1.5px solid ${T.outlineVariant}40`, color: T.secondary,
                  fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.75rem',
                  display: 'flex', alignItems: 'center', gap: 0.5, transition: 'all 0.15s',
                  '&:hover': { bgcolor: T.surfaceLow },
                }}>
                  <Icon name="add" size={14} sx={{ color: T.outline }} /> Fase toevoegen
                </Box>
              </Box>

              {/* Phases */}
              {templateTree?.phases?.map(phase => (
                <Accordion key={phase.id} defaultExpanded sx={{
                  mb: 1, borderRadius: '0.75rem !important', boxShadow: T.shadow,
                  '&:before': { display: 'none' },
                  '& .MuiAccordionSummary-root': { minHeight: 44 },
                }}>
                  <AccordionSummary expandIcon={<Icon name="expand_more" size={18} sx={{ color: T.outline }} />}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 700, color: T.text, flex: 1 }}>{phase.name}</Typography>
                    <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, color: T.primary, bgcolor: T.primaryContainer, px: 1, py: 0.2, borderRadius: '9999px', mr: 1 }}>
                      {phase.tasks?.length || 0} taken
                    </Typography>
                    <Tooltip title="Fase verwijderen">
                      <Box onClick={(e) => { e.stopPropagation(); handleDeletePhase(phase.id); }} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
                        <Icon name="delete" size={16} sx={{ color: T.outlineVariant }} />
                      </Box>
                    </Tooltip>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {phase.tasks?.map(task => (
                      <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, p: 1, borderRadius: '0.5rem', bgcolor: T.surfaceLow }}>
                        <TextField
                          value={task.name} size="small" variant="standard"
                          onChange={(e) => handleUpdateTask(task.id, { name: e.target.value })}
                          sx={{ flex: 1, '& .MuiInput-underline:before': { borderBottom: 'none' }, '& .MuiInput-input': { fontFamily: T.fontBody, fontSize: '0.78rem' } }}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select value={task.role || ''} displayEmpty onChange={(e) => handleUpdateTask(task.id, { role: e.target.value })} sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', borderRadius: '0.35rem' }}>
                            <MenuItem value=""><em>Geen rol</em></MenuItem>
                            {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                          </Select>
                        </FormControl>
                        <TextField
                          value={task.estimated_hours} size="small" type="number"
                          onChange={(e) => handleUpdateTask(task.id, { estimated_hours: parseFloat(e.target.value) || 0 })}
                          sx={{ width: 65, ...inputSx }}
                          InputProps={{ endAdornment: <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline }}>u</Typography> }}
                        />
                        <Box onClick={() => handleDeleteTask(task.id)} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
                          <Icon name="delete" size={16} sx={{ color: T.outlineVariant }} />
                        </Box>
                      </Box>
                    ))}
                    <Box onClick={() => handleAddTask(phase.id)} sx={{
                      mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                      fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: T.primary,
                      '&:hover': { opacity: 0.7 },
                    }}>
                      <Icon name="add" size={14} sx={{ color: T.primary }} /> Taak toevoegen
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default WorkflowTemplateManager;
